import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Lock, 
  LayoutDashboard, 
  TrendingUp, 
  FileText, 
  LogOut,
  RefreshCw,
  Check,
  X,
  Clock,
  DollarSign
} from 'lucide-react';
import { FaBitcoin, FaEthereum } from 'react-icons/fa';
import { SiTether } from 'react-icons/si';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COIN_ICONS = {
  bitcoin: { icon: FaBitcoin, color: '#F7931A', symbol: 'BTC' },
  ethereum: { icon: FaEthereum, color: '#627EEA', symbol: 'ETH' },
  tether: { icon: SiTether, color: '#26A17B', symbol: 'USDT' },
};

export default function AdminPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [savedPassword, setSavedPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({ total_requests: 0, pending_requests: 0, completed_requests: 0 });
  const [tradeRequests, setTradeRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [rates, setRates] = useState([]);
  const [manualRates, setManualRates] = useState({
    bitcoin: { buy_rate: '', sell_rate: '' },
    ethereum: { buy_rate: '', sell_rate: '' },
    tether: { buy_rate: '', sell_rate: '' },
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/login`, { password });
      setIsLoggedIn(true);
      setSavedPassword(password);
      toast.success('Login successful!');
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, requestsRes, ratesRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/stats`),
        axios.get(`${API_URL}/api/trade-requests`),
        axios.get(`${API_URL}/api/rates`),
        axios.get(`${API_URL}/api/payments/transactions?admin_password=${encodeURIComponent(savedPassword)}`),
      ]);
      setStats(statsRes.data);
      setTradeRequests(requestsRes.data.requests);
      setRates(ratesRes.data.rates);
      setPayments(paymentsRes.data.transactions);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    }
  };

  const updateRequestStatus = async (tradeId, status) => {
    try {
      await axios.patch(`${API_URL}/api/trade-requests/${tradeId}?status=${status}`);
      toast.success(`Request marked as ${status}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const saveManualRate = async (coinId) => {
    const rate = manualRates[coinId];
    if (!rate.buy_rate || !rate.sell_rate) {
      toast.error('Please enter both buy and sell rates');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/rates`, {
        coin_id: coinId,
        buy_rate: parseFloat(rate.buy_rate),
        sell_rate: parseFloat(rate.sell_rate),
      });
      toast.success(`Rate for ${COIN_ICONS[coinId].symbol} updated`);
      fetchData();
    } catch (error) {
      toast.error('Failed to update rate');
    }
  };

  const deleteManualRate = async (coinId) => {
    try {
      await axios.delete(`${API_URL}/api/admin/rates/${coinId}`);
      toast.success('Manual rate removed, using live rates');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove rate');
    }
  };

  useEffect(() => {
    if (isLoggedIn && savedPassword) {
      fetchData();
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, savedPassword]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-NG', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="glass-card-gold p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary-main/10 flex items-center justify-center">
              <Lock size={32} className="text-primary-main" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-text-primary">Admin Login</h1>
            <p className="text-text-secondary mt-2">Enter your password to access the dashboard</p>
          </div>

          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              className="input-dark w-full mb-4"
              data-testid="admin-password-input"
            />
            <motion.button
              type="submit"
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="btn-gold w-full"
              data-testid="admin-login-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </motion.button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-20 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-heading text-3xl font-bold text-text-primary">Admin Dashboard</h1>
          <button
            onClick={() => setIsLoggedIn(false)}
            className="flex items-center gap-2 text-text-secondary hover:text-accent-danger transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 overflow-x-auto">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'requests', label: 'Trade Requests', icon: FileText },
            { id: 'payments', label: 'Payments', icon: DollarSign },
            { id: 'rates', label: 'Manage Rates', icon: TrendingUp },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-primary-main text-background'
                  : 'bg-background-paper text-text-secondary hover:text-text-primary'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="glass-card p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary-main/20 flex items-center justify-center">
                    <FileText size={24} className="text-secondary-main" />
                  </div>
                  <div>
                    <p className="text-text-muted text-sm">Total Requests</p>
                    <p className="font-heading text-3xl font-bold text-text-primary">{stats.total_requests}</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-card p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-main/20 flex items-center justify-center">
                    <Clock size={24} className="text-primary-main" />
                  </div>
                  <div>
                    <p className="text-text-muted text-sm">Pending</p>
                    <p className="font-heading text-3xl font-bold text-primary-main">{stats.pending_requests}</p>
                  </div>
                </div>
              </div>
              
              <div className="glass-card p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent-success/20 flex items-center justify-center">
                    <Check size={24} className="text-accent-success" />
                  </div>
                  <div>
                    <p className="text-text-muted text-sm">Completed</p>
                    <p className="font-heading text-3xl font-bold text-accent-success">{stats.completed_requests}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Current Rates */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-xl font-semibold text-text-primary">Current Rates</h3>
                <button onClick={fetchData} className="text-primary-main hover:text-primary-light">
                  <RefreshCw size={18} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {rates.map((rate) => {
                  const coin = COIN_ICONS[rate.coin_id];
                  const CoinIcon = coin?.icon || FaBitcoin;
                  return (
                    <div key={rate.coin_id} className="bg-background-paper rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <CoinIcon size={24} style={{ color: coin?.color }} />
                        <span className="font-semibold text-text-primary">{rate.symbol}</span>
                      </div>
                      <div className="space-y-1 text-sm">
                        <p className="text-text-secondary">Buy: <span className="text-accent-success font-mono">{formatPrice(rate.buy_rate)}</span></p>
                        <p className="text-text-secondary">Sell: <span className="text-accent-danger font-mono">{formatPrice(rate.sell_rate)}</span></p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}

        {/* Trade Requests Tab */}
        {activeTab === 'requests' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-semibold text-text-primary">Trade Requests</h3>
              <button onClick={fetchData} className="text-primary-main hover:text-primary-light flex items-center gap-2">
                <RefreshCw size={18} />
                Refresh
              </button>
            </div>

            {tradeRequests.length === 0 ? (
              <p className="text-text-muted text-center py-12">No trade requests yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Date</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Type</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Crypto</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Amount</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Status</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradeRequests.map((request) => (
                      <tr key={request.id} className="border-b border-border/50 hover:bg-background-paper/50">
                        <td className="py-4 px-4 text-text-secondary text-sm">{formatDate(request.created_at)}</td>
                        <td className="py-4 px-4">
                          <p className="text-text-primary font-medium">{request.full_name}</p>
                          <p className="text-text-muted text-xs">{request.email}</p>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            request.trade_type === 'buy' ? 'bg-accent-success/20 text-accent-success' : 'bg-accent-danger/20 text-accent-danger'
                          }`}>
                            {request.trade_type.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-text-primary">{request.crypto_type}</td>
                        <td className="py-4 px-4 text-text-primary font-mono">{request.amount}</td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            request.status === 'pending' ? 'bg-primary-main/20 text-primary-main' :
                            request.status === 'completed' ? 'bg-accent-success/20 text-accent-success' :
                            'bg-accent-danger/20 text-accent-danger'
                          }`}>
                            {request.status}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateRequestStatus(request.id, 'completed')}
                              className="p-2 rounded bg-accent-success/20 text-accent-success hover:bg-accent-success/30"
                              title="Mark as completed"
                            >
                              <Check size={14} />
                            </button>
                            <button
                              onClick={() => updateRequestStatus(request.id, 'cancelled')}
                              className="p-2 rounded bg-accent-danger/20 text-accent-danger hover:bg-accent-danger/30"
                              title="Cancel request"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-semibold text-text-primary">Payment Transactions</h3>
              <button onClick={fetchData} className="text-primary-main hover:text-primary-light flex items-center gap-2">
                <RefreshCw size={18} />
                Refresh
              </button>
            </div>

            {payments.length === 0 ? (
              <p className="text-text-muted text-center py-12">No payment transactions yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Date</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Reference</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Customer</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Amount</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Crypto</th>
                      <th className="text-left py-3 px-4 text-text-muted text-sm font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment) => (
                      <tr key={payment.id} className="border-b border-border/50 hover:bg-background-paper/50">
                        <td className="py-4 px-4 text-text-secondary text-sm">{formatDate(payment.created_at)}</td>
                        <td className="py-4 px-4 text-text-primary font-mono text-xs">{payment.reference}</td>
                        <td className="py-4 px-4">
                          <p className="text-text-primary font-medium">{payment.full_name}</p>
                          <p className="text-text-muted text-xs">{payment.email}</p>
                        </td>
                        <td className="py-4 px-4 text-text-primary font-mono">{formatPrice(payment.amount)}</td>
                        <td className="py-4 px-4 text-text-primary">{payment.crypto_type}</td>
                        <td className="py-4 px-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            payment.payment_status === 'paid' ? 'bg-accent-success/20 text-accent-success' :
                            payment.payment_status === 'failed' ? 'bg-accent-danger/20 text-accent-danger' :
                            'bg-primary-main/20 text-primary-main'
                          }`}>
                            {payment.payment_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}

        {/* Manage Rates Tab */}
        {activeTab === 'rates' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="glass-card p-6 mb-6">
              <h3 className="font-heading text-xl font-semibold text-text-primary mb-2">Manual Rate Override</h3>
              <p className="text-text-secondary mb-6">
                Set custom buy/sell rates. These will override the live CoinGecko rates.
              </p>

              <div className="space-y-6">
                {Object.entries(COIN_ICONS).map(([coinId, coin]) => {
                  const CoinIcon = coin.icon;
                  return (
                    <div key={coinId} className="bg-background-paper rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-4">
                        <CoinIcon size={24} style={{ color: coin.color }} />
                        <span className="font-semibold text-text-primary">{coin.symbol}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-text-muted text-sm mb-1">Buy Rate (NGN)</label>
                          <input
                            type="number"
                            value={manualRates[coinId].buy_rate}
                            onChange={(e) => setManualRates(prev => ({
                              ...prev,
                              [coinId]: { ...prev[coinId], buy_rate: e.target.value }
                            }))}
                            placeholder="Enter buy rate"
                            className="input-dark w-full"
                          />
                        </div>
                        <div>
                          <label className="block text-text-muted text-sm mb-1">Sell Rate (NGN)</label>
                          <input
                            type="number"
                            value={manualRates[coinId].sell_rate}
                            onChange={(e) => setManualRates(prev => ({
                              ...prev,
                              [coinId]: { ...prev[coinId], sell_rate: e.target.value }
                            }))}
                            placeholder="Enter sell rate"
                            className="input-dark w-full"
                          />
                        </div>
                        <div className="flex items-end gap-2">
                          <button
                            onClick={() => saveManualRate(coinId)}
                            className="px-4 py-2 bg-primary-main text-background rounded-lg font-medium hover:brightness-110 transition-all"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => deleteManualRate(coinId)}
                            className="px-4 py-2 bg-accent-danger/20 text-accent-danger rounded-lg font-medium hover:bg-accent-danger/30 transition-all"
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
